import axios from '../../app/axios';
import { endpoints } from '../../utils/constants';
import RequestHeaders from '../../enums/RequestHeaders';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import ProformaInvoiceDC from 'src/db/entities/proforma_invoice/ProformaInvoiceDC';
import {
  mapProformaInvoiceDCToEntity,
  mapProformaInvoiceDraftToCreatePayload,
  mapProformaInvoiceDraftToUpdatePayload,
} from 'src/db/entities/proforma_invoice/mapper';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import { ProformaInvoiceUpsertPayload } from './types';
import { getPrice } from '../proposalsAPI/api';
import type { DISCOUNT_TYPES, ProposalItemEntity } from 'src/db/entities/proposal/ProposalEntity';

interface PriceResult {
  total: number;
  subtotal: number;
  subtotalWithDiscount: number;
  tax: number;
}

export interface ProformaInvoiceResponse {
  invoices: ProformaInvoiceEntity[];
}

export const createProformaInvoice = async (
  draft: ProformaInvoiceDraft,
  workspaceId: string
): Promise<ProformaInvoiceEntity> => {
  const payload: ProformaInvoiceUpsertPayload = mapProformaInvoiceDraftToCreatePayload(draft);

  const response = await axios.post<ProformaInvoiceDC>(endpoints.proforma_invoice.list, payload, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return mapProformaInvoiceDCToEntity(response.data, workspaceId);
};

export const updateProformaInvoice = async (
  draft: ProformaInvoiceDraft,
  initialDraft: ProformaInvoiceDraft,
  workspaceId: string
): Promise<ProformaInvoiceEntity> => {
  const payload: ProformaInvoiceUpsertPayload = mapProformaInvoiceDraftToUpdatePayload(
    draft,
    initialDraft
  );

  const response = await axios.patch<ProformaInvoiceDC>(endpoints.proforma_invoice.list, payload, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return mapProformaInvoiceDCToEntity(response.data, workspaceId);
};

/** PATCH with a ready payload, e.g. the quote content copied into its invoice. */
export const patchProformaInvoice = async (
  payload: ProformaInvoiceUpsertPayload,
  workspaceId: string
): Promise<ProformaInvoiceEntity> => {
  const response = await axios.patch<ProformaInvoiceDC>(endpoints.proforma_invoice.list, payload, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return mapProformaInvoiceDCToEntity(response.data, workspaceId);
};

export const deleteProformaInvoice = async (uuid: string, workspaceId: string): Promise<void> => {
  await axios.delete<true>(endpoints.proforma_invoice.list, {
    data: [uuid],
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });
};

export const fetchProformaInvoice = async (
  uuid: string,
  workspaceId: string
): Promise<ProformaInvoiceEntity> => {
  const response = await axios.get<ProformaInvoiceDC>(
    endpoints.proforma_invoice.get.replace('{uuid}', uuid),
    {
      headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
    }
  );

  return mapProformaInvoiceDCToEntity(response.data, workspaceId);
};

const postInvoiceAction = async (
  url: string,
  uuid: string,
  workspaceId: string
): Promise<ProformaInvoiceEntity> => {
  const response = await axios.post<ProformaInvoiceDC>(url.replace('{uuid}', uuid), undefined, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return mapProformaInvoiceDCToEntity(response.data, workspaceId);
};

// Clears the review mark; the invoice content and PDF stay as they are.
export const dismissProformaInvoiceReview = (uuid: string, workspaceId: string) =>
  postInvoiceAction(endpoints.proforma_invoice.dismissReview, uuid, workspaceId);

// Copies the linked quote's content and settings into the invoice and clears the mark.
export const updateProformaInvoiceFromQuote = (uuid: string, workspaceId: string) =>
  postInvoiceAction(endpoints.proforma_invoice.updateFromQuote, uuid, workspaceId);

export const calculateProformaInvoicePrice = async (params: {
  items: ProformaInvoiceDraft['items'];
  discountType: DISCOUNT_TYPES;
  discountAmount: number;
  includesTax: boolean;
  taxRate: number;
}): Promise<PriceResult> => {
  const proposalItems: ProposalItemEntity[] = params.items.map((item) => ({
    description: item.description ?? '',
    qty: item.qty,
    unitPrice: item.amount,
  }));

  const { discountType, discountAmount, includesTax, taxRate } = params;

  const response = await getPrice({
    items: proposalItems,
    discountType,
    discountAmount,
    includesTax,
    taxRate,
  });

  return {
    total: response.total,
    subtotal: response.subtotal,
    subtotalWithDiscount: response.subtotalWithDiscount,
    tax: response.tax,
  };
};
